import React, { Fragment, useEffect } from "react";
import { Row, Col, Table } from "reactstrap";
import { Link } from "react-router-dom";

import {
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

interface TableContainerProps {
  columns: any[];
  data: any[];
  customPageSize?: number;
  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  thClass?: string;
  divClass?: string;
  manualPagination?: boolean;
  pageCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  currentPage?: number;
  totalRecords?: number;
  showPageSizeOptions?: boolean;
  pageSizeOptions?: number[];
}

const TableContainer = ({
  columns,
  data,
  customPageSize = 10,
  tableClass = "",
  theadClass = "",
  trClass = "",
  thClass = "",
  divClass = "",
  manualPagination = false,
  pageCount = 0,
  onPageChange,
  onPageSizeChange,
  currentPage = 0,
  totalRecords = 0,
  showPageSizeOptions = true,
  pageSizeOptions = [5, 10, 25, 50, 100],
}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [pageSize, setPageSize] = React.useState(customPageSize);

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  // ✅ Fix: For manual pagination, we don't want internal pagination
  const table = useReactTable({
    columns,
    data,
    filterFns: { fuzzy: fuzzyFilter },
    state: { 
      columnFilters, 
      globalFilter,
      // ✅ Control pagination state manually
      pagination: {
        pageIndex: currentPage,
        pageSize: pageSize,
      }
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // ✅ Disable internal pagination when using manual pagination
    getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // ✅ Manual pagination flag
    manualPagination: manualPagination,
    // ✅ We have our own pagination controls
    autoResetPageIndex: false,
  });

  const {
    getHeaderGroups,
    getRowModel,
    setPageSize: setTablePageSize,
  } = table;

  // Sync customPageSize with internal state
  useEffect(() => {
    console.log('📊 Syncing page size:', customPageSize);
    setPageSize(customPageSize);
    if (!manualPagination) {
      setTablePageSize(customPageSize);
    }
  }, [customPageSize, manualPagination, setTablePageSize]);

  // DEBUG: Log all important props and state
  useEffect(() => {
    console.log('🔍 TableContainer Debug:');
    console.log('  customPageSize prop:', customPageSize);
    console.log('  pageSize state:', pageSize);
    console.log('  totalRecords:', totalRecords);
    console.log('  currentPage:', currentPage);
    console.log('  manualPagination:', manualPagination);
    console.log('  pageCount:', pageCount);
    console.log('  data length:', data?.length || 0);
    console.log('  start calculation:', currentPage * pageSize + 1);
    console.log('  end calculation:', Math.min((currentPage + 1) * pageSize, totalRecords));
    console.log('  ---');
  }, [customPageSize, pageSize, totalRecords, currentPage, manualPagination, pageCount, data]);

  // Handle page size change
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = Number(e.target.value);
    console.log('📊 Page size changed to:', newSize);
    setPageSize(newSize);
    
    if (manualPagination && onPageSizeChange) {
      console.log('📊 Calling parent onPageSizeChange with:', newSize);
      onPageSizeChange(newSize);
    } else {
      setTablePageSize(newSize);
    }
  };

  const handlePageClick = (page: number) => {
    console.log('📊 Page clicked:', page);
    if (manualPagination && onPageChange) {
      onPageChange(page);
    }
  };

  // Use the current pageSize state for calculations
  const start = totalRecords > 0 ? currentPage * pageSize + 1 : 0;
  const end = Math.min((currentPage + 1) * pageSize, totalRecords);

  // ✅ For manual pagination, we need to slice the data ourselves
  const displayData = manualPagination 
    ? data // Show all data (parent handles pagination)
    : data; // For internal pagination, tanstack handles it

  return (
    <Fragment>
      {showPageSizeOptions && (
        <Row className="mb-2">
          <Col sm="auto">
            <div className="d-flex align-items-center gap-2">
              <label className="text-muted mb-0" style={{ fontSize: '14px' }}>
                Show
              </label>
              <select
                className="form-select form-select-sm"
                value={pageSize}
                onChange={handlePageSizeChange}
                style={{ width: 'auto' }}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <label className="text-muted mb-0" style={{ fontSize: '14px' }}>
                entries
              </label>
            </div>
          </Col>
        </Row>
      )}

      <div className={divClass}>
        <Table hover className={tableClass}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => (
                  <th 
                    key={header.id} 
                    className={thClass}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{ cursor: 'pointer' }}
                  >
                    {header.isPlaceholder ? null : (
                      <span>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: ' 🔼',
                          desc: ' 🔽',
                        }[header.column.getIsSorted() as string] ?? ''}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length > 0 ? (
              getRowModel().rows.map((row: any) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell: any) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
        <Col sm={6}>
          <div className="text-muted">
            Showing {start} to {end} of {totalRecords} Results
          </div>
        </Col>
        <Col sm={6}>
          <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-end mb-0">
            <li className={`page-item ${currentPage === 0 ? 'disabled' : ''}`}>
              <Link 
                to="#" 
                className="page-link" 
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage > 0) handlePageClick(currentPage - 1);
                }}
              >
                Previous
              </Link>
            </li>
            
            {Array.from({ length: pageCount }, (_, i) => i).map((page) => (
              <li className="page-item" key={page}>
                <Link
                  to="#"
                  className={`page-link ${currentPage === page ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageClick(page);
                  }}
                >
                  {page + 1}
                </Link>
              </li>
            ))}
            
            <li className={`page-item ${currentPage === pageCount - 1 ? 'disabled' : ''}`}>
              <Link 
                to="#" 
                className="page-link" 
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage < pageCount - 1) handlePageClick(currentPage + 1);
                }}
              >
                Next
              </Link>
            </li>
          </ul>
        </Col>
      </Row>
    </Fragment>
  );
};

export default TableContainer;